/* eslint-disable quotes */
import ApiService from "./api.service";
// import ApiMockService from "./api.mock.service"
/**
 * DiseaseService is an object that stores all functions used to manage diseases and their supplement interactions.
 *
 * getAll --> Makes a ._GET request to a specific endpoint to retrieve a list of diseases with given parameters.
 * Returns the list of diseases.
 *
 * save --> Performs a POST request to the endpoint to send data along with the specified language (information to create or update a resource on the server).
 * The function waits for the API response and once received, returns the saved or updated data.
 *
 * remove --> Sends a GET request to the endpoint to delete the disease by ID with the specified language.
 * The function waits for the API response and returns the result.
 *
 * getSupplementInteraction --> Sends a GET request to the endpoint with the supplement ID to retrieve the interaction between the supplement and disease.
 * Returns the interaction data.
 *
 * saveSupplementInteraction --> Performs a POST request to the endpoint with the supplement ID to add information about the supplement-disease interaction.
 * Returns success or error indicating whether the information was saved.
 *
 * removeSupplementInteraction --> Sends a POST request to the endpoint with the supplement ID to delete the supplement-disease interaction.
 * Returns success or error indicating whether the information was removed.
 *
 * deleteListOfDiseasesPerSupplement --> Performs a POST request to batch delete interactions between a supplement and multiple diseases.
 * Returns the API response.
 */

const DiseaseService = {
  /**
   * Retrieves all diseases with optional query parameters.
   * @param {Object} params Query parameters for filtering diseases.
   * @returns {Promise<Object>} The response data containing the list of diseases.
   */
  getAll: async function (params) {
    const response = await ApiService._get("api/admin/disease/get-all", params);
    return response.data;
  },

  /**
   * Retrieves a disease by ID (resolved against the full list).
   * @param {*} id Disease ID
   * @returns Disease object or null if not found
   */
  getById: async function (id) {
    const response = await this.getAll();
    const list = response?.data ? response.data : response || [];
    return list.find((d) => String(d.id) === String(id)) || null;
  },

  /**
   * Saves a disease record (create or update).
   * @param {Object} params Disease data to be saved.
   * @param {string|null} [lang=null] Optional language code.
   * @returns {Promise<Object>} The saved disease data.
   */
  save: async function (params, lang = null) {
    const response = await ApiService.post("api/admin/disease/save", params, lang);
    return response.data;
  },

  /**
   * Deletes a disease by ID.
   * @param {number|string} id Identifier of the disease to delete.
   * @param {string|null} [lang=null] Optional language code.
   * @returns {Promise<Object>} The API response after deletion.
   */
  remove: async function (id, lang = null) {
    const response = await ApiService.get("api/admin/disease/delete?id=" + id, lang);
    return response.data;
  },

  /**
   * Retrieves supplement-disease interaction data.
   * @param {number|string} id Supplement identifier.
   * @param {string|null} [lang=null] Optional language code.
   * @returns {Promise<Object>} Interaction data for the given supplement.
   */
  getSupplementInteraction: async function (id, lang = null) {
    const response = await ApiService.get("api/admin/disease/get-supplement-interaction?supplement_id=" + id, lang);
    return response.data;
  },

  /**
   * Saves a supplement-disease interaction.
   * @param {Object} params Interaction data to be saved.
   * @param {string|null} [lang=null] Optional language code.
   * @returns {Promise<Object>} The saved interaction data.
   */
  saveSupplementInteraction: async function (params, lang = null) {
    const response = await ApiService.post("api/admin/disease/save-supplement-interaction", params, lang);
    return response.data;
  },

  /**
   * Removes a supplement-disease interaction.
   * @param {Object} params Parameters identifying the interaction to remove.
   * @param {string|null} [lang=null] Optional language code.
   * @returns {Promise<Object>} The API response after removal.
   */
  removeSupplementInteraction: async function (params, lang = null) {
    const response = await ApiService.post("api/admin/disease/delete-supplement-interaction", params, lang);
    return response.data;
  },

  /**
   * Deletes a list of interactions between a supplement and diseases.
   * @param {Object} params Parameters containing supplementId and ids array.
   * @param {*} params.supplementId Supplement ID.
   * @param {*} params.ids Array of interaction IDs to delete.
   * @returns {Promise<Object>} API response after batch deletion.
   */
  deleteListOfDiseasesPerSupplement: async function (params) {
    const response = await ApiService.post("api/admin/disease/delete-list-disease-interaction", params);
    return response.data;
  },
};

export default DiseaseService;
